import { z } from 'zod';

import {
  IsoDateTimeSchema,
  LocalDateSchema,
  LocalTimeSchema,
  type LocalDate,
  type LocalTime,
} from '../common/datetime';
import { TimezoneSchema } from '../common/fields';
import {
  AdminId,
  AttendanceMarkId,
  RegularizationRequestId,
  SiteId,
  TerminalId,
  WorkerId,
} from '../common/ids';
import { CursorQuerySchema, pageSchema } from '../common/pagination';
import { DevicePlatform } from '../staff/devices';
import {
  AttendanceActionSchema,
  AttendanceFlagSchema,
  AttendanceMarkStatusSchema,
  IdempotencyKeySchema,
  LocationStatusSchema,
  MarkSiteRefSchema,
  VppModeSchema,
} from '../staff/vpp';

import { AutonomousPeriodSchema } from './terminals';
import { MarkDisplayStatusSchema } from './workers';

/**
 * Marcaciones tal como las ve el Admin (004 · T077, ampliado en 005 · T007).
 *
 * La 004 solo leía; la 005 añade el dashboard, las correcciones, las anulaciones, las marcas
 * manuales y la exportación (`contracts/admin-attendance.md`). `attendance_marks` sigue siendo
 * append-only: corregir es una marca nueva con `supersedes_mark_id`.
 *
 * Los enums se reutilizan de `staff/vpp.ts` (barrel único, sin subpaths): `AttendanceActionSchema`,
 * `AttendanceMarkStatusSchema`, `AttendanceFlagSchema`, `LocationStatusSchema`, `VppModeSchema` y
 * `MarkSiteRefSchema` son exactamente los mismos valores que persiste la tabla, y duplicarlos aquí
 * sería la manera más fácil de que diverjan (wiring §8.5). `MarkDisplayStatusSchema` vive en
 * `workers.ts` para no cerrar un ciclo de imports (ver su comentario).
 *
 * **Coordenadas**: solo en el detalle y solo con `attendance.location.read` (FR-028); sin el
 * permiso viajan `null` con `coordinates_hidden: true`. Nunca en listados ni en la exportación.
 */

// ---------------------------------------------------------------------------
// Enums propios de la vista de Admin
// ---------------------------------------------------------------------------

/**
 * Origen de la marca (`attendance_marks.source`). `MANUAL` es de la feature 005; la 004 solo
 * escribe `VPP`, pero la vista tiene que saber pintar las dos desde ya.
 */
export const AttendanceMarkSourceSchema = z.enum(['VPP', 'MANUAL']);
export type AttendanceMarkSource = z.infer<typeof AttendanceMarkSourceSchema>;

// ---------------------------------------------------------------------------
// Referencias
// ---------------------------------------------------------------------------

/** Administrador que creó o anuló una marca. */
export const MarkAdminRefSchema = z.object({
  id: AdminId,
  name: z.string(),
});
export type MarkAdminRef = z.infer<typeof MarkAdminRefSchema>;

/**
 * Trabajador de la marca. Sin documento: para eso está la ficha del trabajador.
 * `anonymized` (005): los nombres ya son los marcadores de la anonimización.
 */
export const MarkWorkerRefSchema = z.object({
  id: WorkerId,
  first_names: z.string(),
  last_names: z.string(),
  internal_code: z.string().nullable(),
  anonymized: z.boolean(),
});
export type MarkWorkerRef = z.infer<typeof MarkWorkerRefSchema>;

/** Marcador donde ocurrió la marca; `null` en las marcas manuales de la 005. */
export const MarkTerminalRefSchema = z.object({
  id: TerminalId,
  name: z.string(),
});
export type MarkTerminalRef = z.infer<typeof MarkTerminalRefSchema>;

// ---------------------------------------------------------------------------
// Entidad
// ---------------------------------------------------------------------------

export const AttendanceMarkAdminSchema = z.object({
  id: AttendanceMarkId,
  worker: MarkWorkerRefSchema,
  /** `site.timezone` es la zona **actual** de la sede; no se usa para formatear la marca. */
  site: MarkSiteRefSchema,
  /** 005: zona denormalizada de la marca; con esta se formatea `server_timestamp` (research R-4). */
  site_timezone: TimezoneSchema,
  terminal: MarkTerminalRefSchema.nullable(),
  type: AttendanceActionSchema,
  /** Hora oficial: reloj del servidor en UTC. */
  server_timestamp: IsoDateTimeSchema,
  /** Día operativo de la sede (`YYYY-MM-DD`), ya calculado al crear la marca. */
  operative_day: z.iso.date(),
  source: AttendanceMarkSourceSchema,
  /** `null` ⟺ `source = 'MANUAL'`. */
  vpp_mode: VppModeSchema.nullable(),
  status: AttendanceMarkStatusSchema,
  /** 005: estado + origen para el badge (FR-024), calculado por el servidor. */
  display_status: MarkDisplayStatusSchema,
  /** Motivos de observación; vacío salvo que `status = 'FLAGGED'`. */
  flags: z.array(AttendanceFlagSchema),
  location_status: LocationStatusSchema,
  /** 005: la marca que esta corrige, si es una corrección. */
  supersedes_mark_id: AttendanceMarkId.nullable(),
  /** 005: la corrección que reemplazó a esta (enlace "Reemplazada → corrección", US2-4). */
  superseded_by_mark_id: AttendanceMarkId.nullable(),
});
export type AttendanceMarkAdmin = z.infer<typeof AttendanceMarkAdminSchema>;

/**
 * Ubicación de la marca en el detalle. `distance_m` y `accuracy_m` son visibles también para
 * VIEWER; `latitude`/`longitude` solo con `attendance.location.read` (si no, `null` y
 * `coordinates_hidden: true`). `purged_at` ≠ `null` ⇒ borradas por retención (FR-006, FR-020).
 */
export const MarkLocationSchema = z.object({
  status: LocationStatusSchema,
  distance_m: z.number().int().nonnegative().nullable(),
  accuracy_m: z.number().int().nonnegative().nullable(),
  latitude: z.number().min(-90).max(90).nullable(),
  longitude: z.number().min(-180).max(180).nullable(),
  coordinates_hidden: z.boolean(),
  purged_at: IsoDateTimeSchema.nullable(),
});
export type MarkLocation = z.infer<typeof MarkLocationSchema>;

/**
 * Resumen VPP sin datos criptográficos (FR-006). `null` en marcas `MANUAL`.
 * `frames_emitted`/`frames_required`: `frame_count`/`required_frames` del challenge (modo NORMAL).
 * `autonomous_*`: códigos verificados y período sin conexión del marcador (modo AUTONOMOUS).
 */
export const MarkVppSummarySchema = z
  .object({
    mode: VppModeSchema,
    frames_emitted: z.number().int().min(4).max(10).nullable(),
    frames_required: z.number().int().min(3).max(10).nullable(),
    autonomous_frames_count: z.number().int().nonnegative().nullable(),
    autonomous_period: AutonomousPeriodSchema.nullable(),
  })
  .nullable();
export type MarkVppSummary = z.infer<typeof MarkVppSummarySchema>;

/** Un eslabón de la cadena de corrección (original → … → vigente). */
export const MarkChainEntrySchema = z.object({
  id: AttendanceMarkId,
  type: AttendanceActionSchema,
  server_timestamp: IsoDateTimeSchema,
  site_timezone: TimezoneSchema,
  source: AttendanceMarkSourceSchema,
  status: AttendanceMarkStatusSchema,
  /** "Fecha y hora de la corrección" (US3-4). */
  created_at: IsoDateTimeSchema,
  /** Admin que la creó (marcas `MANUAL`); `null` en las VPP. */
  created_by: MarkAdminRefSchema.nullable(),
  reason: z.string().nullable(),
});
export type MarkChainEntry = z.infer<typeof MarkChainEntrySchema>;

/**
 * 007 · bloque `regularization` del detalle de marca (research R-26). Lo calcula el servidor: la UI
 * muestra "Pedir regularización" solo con `can_ask` y no recalcula huecos.
 *
 * Usa `RegularizationRequestId` de `common/ids.ts` (el mismo esquema que `RegularizationIdSchema`
 * de `staff/regularizations.ts`) para no cerrar un ciclo de imports: ese archivo importa
 * `LocalDateSchema`/`LocalTimeSchema` de aquí.
 */
export const MarkRegularizationInfoSchema = z.object({
  /** `null`: la marca no es ancla de un hueco. */
  missing_type: AttendanceActionSchema.nullable(),
  /** Hueco + ≤ 60 días + trabajador ACTIVE + sin abierta + `attendance.write` + empresa ACTIVE. */
  can_ask: z.boolean(),
  open_request: z
    .object({ id: RegularizationRequestId, status: z.enum(['ASKED', 'PENDING']) })
    .nullable(),
  /** La solicitud que creó esta marca (marca `MANUAL` aprobada). */
  source_request_id: RegularizationRequestId.nullable(),
});
export type MarkRegularizationInfo = z.infer<typeof MarkRegularizationInfoSchema>;

/**
 * Detalle de una marca (005, reformado). **Cambia de forma respecto a la 004**: `accuracy_m`,
 * `autonomous_frames_count` y `autonomous_period` pasan a `location.accuracy_m`,
 * `vpp.autonomous_frames_count` y `vpp.autonomous_period`. Nunca salen `challenge_id`,
 * `device_id`, `terminal_session_id`, `idempotency_key`, `device_timestamp`, slots ni nonces.
 */
export const AttendanceMarkDetailSchema = AttendanceMarkAdminSchema.extend({
  device: z.object({ platform: DevicePlatform, enrolled_at: IsoDateTimeSchema }).nullable(),
  vpp: MarkVppSummarySchema,
  location: MarkLocationSchema,
  created_by: MarkAdminRefSchema.nullable(),
  reason: z.string().nullable(),
  voided: z
    .object({ at: IsoDateTimeSchema, by: MarkAdminRefSchema, reason: z.string() })
    .nullable(),
  /** Original → … → vigente; incluye esta marca. */
  chain: z.array(MarkChainEntrySchema).min(1).max(21),
  /** Indicador derivado "Salida sin entrada" (research R-16); no es un flag. */
  checkout_without_checkin: z.boolean(),
  /** La marca vigente de la cadena, si existe. */
  current_mark_id: AttendanceMarkId.nullable(),
  /** 007 · hueco del que es ancla, solicitud abierta o que la creó y si se puede pedir (R-26). */
  regularization: MarkRegularizationInfoSchema,
});
export type AttendanceMarkDetail = z.infer<typeof AttendanceMarkDetailSchema>;

// ---------------------------------------------------------------------------
// Escritura (005): marca manual, corrección, anulación
// ---------------------------------------------------------------------------

/** Advertencias que el admin debe reconocer antes de escribir (`409 CONFIRMATION_REQUIRED`). */
export const MarkWarningCodeSchema = z.enum([
  'CONSECUTIVE_CHECK_IN',
  'CONSECUTIVE_CHECK_OUT',
  'SITE_NOT_AUTHORIZED',
  'BEFORE_ENROLLMENT',
]);
export type MarkWarningCode = z.infer<typeof MarkWarningCodeSchema>;

/**
 * `LocalDateSchema`/`LocalTimeSchema` viven en `common/datetime.ts` desde la 008 (los necesita
 * `common/schedules.ts` sin crear un ciclo de imports con `admin/companies.ts`); se reexportan aquí
 * para que los imports existentes (`from './attendance'`, `from '../admin/attendance'`) no cambien.
 */
export { LocalDateSchema, LocalTimeSchema };
export type { LocalDate, LocalTime };

/** Motivo obligatorio de una escritura del admin: 1–500 caracteres tras `trim`. */
export const MarkReasonSchema = z.string().trim().min(1).max(500);
export type MarkReason = z.infer<typeof MarkReasonSchema>;

const acknowledgedWarningsField = z.array(MarkWarningCodeSchema).max(4).default([]);

/** `POST /marks/manual` (FR-007, US4). */
export const CreateManualMarkBodySchema = z.object({
  worker_id: WorkerId,
  site_id: SiteId,
  type: AttendanceActionSchema,
  date: LocalDateSchema,
  time: LocalTimeSchema,
  reason: MarkReasonSchema,
  idempotency_key: IdempotencyKeySchema,
  acknowledged_warnings: acknowledgedWarningsField,
});
export type CreateManualMarkBody = z.infer<typeof CreateManualMarkBodySchema>;

/**
 * `POST /marks/{id}/corrections` (FR-009, US3). `date`/`time` en la zona vigente de la sede de la
 * marca original. "Sin cambios" no se valida aquí: lo rechaza el servicio con `VALIDATION_ERROR`.
 */
export const CreateCorrectionBodySchema = z.object({
  type: AttendanceActionSchema,
  date: LocalDateSchema,
  time: LocalTimeSchema,
  reason: MarkReasonSchema,
  idempotency_key: IdempotencyKeySchema,
  acknowledged_warnings: acknowledgedWarningsField,
});
export type CreateCorrectionBody = z.infer<typeof CreateCorrectionBodySchema>;

/** `POST /marks/{id}/void` (FR-010). */
export const VoidMarkBodySchema = z.object({ reason: MarkReasonSchema });
export type VoidMarkBody = z.infer<typeof VoidMarkBodySchema>;

/** Cambio de `MISSING_CHECKOUT` provocado por una escritura del admin. */
export const MarkFlagChangeSchema = z.object({
  mark_id: AttendanceMarkId,
  change: z.enum(['MISSING_CHECKOUT_ADDED', 'MISSING_CHECKOUT_CLEARED']),
});
export type MarkFlagChange = z.infer<typeof MarkFlagChangeSchema>;

/**
 * `201`/`200` de manual, corrección y anulación. `mark` es la creada (manual), la **corrección**
 * o la anulada. `idempotent: true` ⇒ la `idempotency_key` ya había creado esa marca.
 */
export const MarkMutationResponseSchema = z.object({
  mark: AttendanceMarkDetailSchema,
  idempotent: z.boolean(),
  flag_changes: z.array(MarkFlagChangeSchema),
});
export type MarkMutationResponse = z.infer<typeof MarkMutationResponseSchema>;

// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------

/** Días operativos inclusivos: `from_day ≤ to_day` (comparación lexicográfica de `YYYY-MM-DD`). */
function dayRangeIsOrdered(q: {
  from_day?: string | undefined;
  to_day?: string | undefined;
}): boolean {
  return !q.from_day || !q.to_day || q.from_day <= q.to_day;
}

const DAY_RANGE_ISSUE = {
  message: 'La fecha final no puede ser anterior a la inicial',
  path: ['to_day'],
};

/**
 * `GET /marks?from_day&to_day&site_id&worker_id&type&status&source&flag&vpp_mode&include_history&cursor&limit`.
 *
 * 005: el rango es en **días operativos** de la sede de cada marca (research R-3) y reemplaza a
 * `from`/`to` (instantes). Sin fechas: día operativo actual de cada sede (US2-1).
 * `include_history = false` ⇒ solo `VALID`/`FLAGGED`. Un `site_id` o `worker_id` ajeno o fuera del
 * alcance no es error: `200` con `items: []` (FR-026).
 */
export const AttendanceMarkListQuerySchema = CursorQuerySchema.extend({
  from_day: z.iso.date().optional(),
  to_day: z.iso.date().optional(),
  site_id: SiteId.optional(),
  worker_id: WorkerId.optional(),
  type: AttendanceActionSchema.optional(),
  status: AttendanceMarkStatusSchema.optional(),
  source: AttendanceMarkSourceSchema.optional(),
  flag: AttendanceFlagSchema.optional(),
  vpp_mode: VppModeSchema.optional(),
  /** "Mostrar historial completo". Query string: `'true'`/`'false'` (y `1/0`, `yes/no`, `on/off`). */
  include_history: z.stringbool().default(false),
}).refine(dayRangeIsOrdered, DAY_RANGE_ISSUE);
export type AttendanceMarkListQuery = z.infer<typeof AttendanceMarkListQuerySchema>;

/** Máximo de días operativos (inclusivos) de una exportación (US5-3). */
export const ATTENDANCE_EXPORT_MAX_DAYS = 90;

const MS_PER_DAY = 86_400_000;

/** Días entre dos `YYYY-MM-DD` (`to − from`), sin zona: ambos se leen como medianoche UTC. */
function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / MS_PER_DAY);
}

/**
 * `GET /marks/export` (FR-015…FR-017). Rango obligatorio de a lo sumo 90 días operativos
 * (`to_day − from_day ≤ 89`, error en `to_day`). `type`, `status`, `source`, `flag`, `vpp_mode` e
 * `include_history` solo aplican a `kind = 'detail'`; el resumen los ignora.
 */
export const AttendanceExportQuerySchema = z
  .object({
    kind: z.enum(['detail', 'summary']),
    format: z.enum(['csv', 'xlsx']),
    from_day: z.iso.date(),
    to_day: z.iso.date(),
    site_id: SiteId.optional(),
    worker_id: WorkerId.optional(),
    type: AttendanceActionSchema.optional(),
    status: AttendanceMarkStatusSchema.optional(),
    source: AttendanceMarkSourceSchema.optional(),
    flag: AttendanceFlagSchema.optional(),
    vpp_mode: VppModeSchema.optional(),
    include_history: z.stringbool().default(false),
  })
  .refine(dayRangeIsOrdered, DAY_RANGE_ISSUE)
  .refine((q) => daysBetween(q.from_day, q.to_day) < ATTENDANCE_EXPORT_MAX_DAYS, {
    message: `El rango no puede superar ${ATTENDANCE_EXPORT_MAX_DAYS} días`,
    path: ['to_day'],
  });
export type AttendanceExportQuery = z.infer<typeof AttendanceExportQuerySchema>;

/** `GET /dashboard?site_id` (US1). Sin `site_id`: todas las sedes del alcance del actor. */
export const DashboardQuerySchema = z.object({ site_id: SiteId.optional() });
export type DashboardQuery = z.infer<typeof DashboardQuerySchema>;

/** Parámetro de ruta `{id}` de marca. */
export const AttendanceMarkIdParamsSchema = z.object({ id: AttendanceMarkId });
export type AttendanceMarkIdParams = z.infer<typeof AttendanceMarkIdParamsSchema>;

// ---------------------------------------------------------------------------
// Responses
// ---------------------------------------------------------------------------

/** `200 Page<AttendanceMarkAdmin>` (`ORDER BY server_timestamp DESC, id DESC`). */
export const AttendanceMarkListResponseSchema = pageSchema(AttendanceMarkAdminSchema);
export type AttendanceMarkListResponse = z.infer<typeof AttendanceMarkListResponseSchema>;

/** Contadores del dashboard; definición exacta de cada uno en research R-17. */
export const DashboardCountersSchema = z.object({
  workers: z.number().int().nonnegative(),
  inside: z.number().int().nonnegative(),
  not_arrived: z.number().int().nonnegative(),
  left: z.number().int().nonnegative(),
  marks_today: z.number().int().nonnegative(),
  /**
   * 008 · trabajadores con tardanza hoy / con falta hoy (turnos ya vencidos) en el alcance
   * (research R-27). `null` ⇒ no hay turnos planificados hoy en el alcance (la UI no muestra los
   * KPIs).
   */
  late_today: z.number().int().nonnegative().nullable(),
  absent_today: z.number().int().nonnegative().nullable(),
});
export type DashboardCounters = z.infer<typeof DashboardCountersSchema>;

/**
 * `200` de `GET /dashboard`. `site_id = null` ⇒ "Todas las sedes (de mi alcance)". Un `site_id`
 * ajeno o fuera del alcance responde contadores en cero y lista vacía (FR-026).
 */
export const DashboardResponseSchema = z.object({
  site_id: SiteId.nullable(),
  counters: DashboardCountersSchema,
  /** Solo vigentes, más recientes primero. */
  latest_marks: z.array(AttendanceMarkAdminSchema).max(10),
  server_time: IsoDateTimeSchema,
});
export type DashboardResponse = z.infer<typeof DashboardResponseSchema>;
